import { payMCopyDateStatusInversed, PayMCopyDateStatusKeys } from '@teinor/erp/types/company/accounting/payMCopy/payMCopyDate'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import NullComponent from '../../../../../../baseComponents/NullComponent'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import TwinTable from '../../../../../../baseComponents/TwinTable'
import { BaseScreenTableSimpleStateless, useBaseScreenTableLogic } from '../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderDate, CustomRenderDisplayPriceWOParams, EditRow } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { DisplayStatus } from '../../../../../../baseComponents/Displays/DisplayStatus'
import useCollectionAndPaymentStatus from '../../../../../../utils/hooks/accounting/useCollectionAndPaymentStatus'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import { ButtonSecondary } from '../../../../../../baseComponents/Button'
import { Link } from 'react-router-dom'
import locations from '../../../../../../utils/globals/locations'
import ModalEditPaymentMethodDate from './ModalEditPaymentMethodDate'
import ModalMarkRemittanceAsPaid from './ModalMarkRemittanceAsPaid'
import useUpdateTableInstance from '../../../../../../utils/hooks/useUpdateTableInstance'
import { PayMCopyDateModelTypeComplete } from './ModalMarkRemittanceAsPaid/ModalSelectRemittanceAsPaid/types'


interface CollectionAndPaymentProps extends ComponentWithPermissions { }

const CollectionAndPayment: React.FC<CollectionAndPaymentProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal, tableInstance, setTableInstance } = useBaseScreenTableLogic()
    const { updateTableInstance } = useUpdateTableInstance({ tableInstance })

    const pageData = {
        title: t('collectionAndPayment', 'Cobros y Pagos'),
        newText: '',
    }

    return (
        <BaseScreenTableSimpleStateless TableComponent={TableCollectionAndPayment} CreateModalComponent={NullComponent} EditModalComponent={ModalEditPaymentMethodDate} {...{ pageData, userPermissions, updateTableInstance }} logicProps={{ openModal, setOpenModal, tableInstance, setTableInstance }} RightHeader={
            <div className='flex items-center gap-x-10'>
                <ButtonSecondary onClick={()=>{setOpenModal({type: 'create', allRowData: {}})}}><TwinTrans transKey='maskAsPaid'>Marcar como pagados</TwinTrans></ButtonSecondary>
                <ButtonChangeToStatusView />
            </div>
        }>
            {openModal?.type === 'create' ? <ModalMarkRemittanceAsPaid setOpened={() => setOpenModal(null)} onSubmit={() => updateTableInstance()}/> : null}
            </BaseScreenTableSimpleStateless>
    )
}


interface ButtonChangeToStatusViewProps { }


const ButtonChangeToStatusView: React.FC<ButtonChangeToStatusViewProps> = () => {
    return (
        <ButtonSecondary className='mr-5'>
            <Link to={locations.remittances + '/main'}>
                <TwinTrans transKey='viewRemittances'>Ver remesas</TwinTrans>
            </Link>
        </ButtonSecondary>
    )
}

interface TableCollectionAndPaymentProps extends TableComponentProps { }

const TableCollectionAndPayment: React.FC<TableCollectionAndPaymentProps> = ({ setTableInstance, userPermissions, setOpened }) => {
    const { collectionAndPaymentStatus } = useCollectionAndPaymentStatus()
    const { t } = useTwinTranslation()

    const columns: ColumnTableSchema<PayMCopyDateModelTypeComplete> = [
        {
            id: 'id',
            dataKey: 'id',
            width: 200,
            label: t('id', 'ID'),
            sortable: true,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('id', 'ID') }
            },
            searchKey: 'PayMCopyDate.id'
        },
        {
            id: 'custom_name',
            dataKey: 'PayMCopy.SellInvoice.custom_name',
            label: t('customNameShort', 'Nombre pers.'),
            simpleLabel: t('customName', 'Nombre personalizado'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('invoice', 'Factura') }
            },
            hideColumnByDefault: true
        },
        {
            id: 'full_assigned_number',
            dataKey: 'PayMCopy.SellInvoice.full_assigned_number',
            label: t('invoice', 'Factura'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('invoice', 'Factura') }
            },
        },
        {
            id: 'CustomerId',
            dataKey: 'PayMCopy.Customer.name',
            label: t('customer', 'Cliente'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customer', 'Cliente') }
            }
        },
        {
            id: 'PayTCopy.name',
            dataKey: 'PayTCopy.name',
            label: t('paymentType', 'Tipo de pago'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('paymentType', 'Tipo de pago') }
            }
        },
        {
            id: 'PayMCopy.name',
            dataKey: 'PayMCopy.name',
            label: t('paymentMethod', 'Método de pago'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('paymentMethod', 'Método de pago') }
            }
        },
        {
            id: 'date',
            dataKey: 'date',
            label: t('date', 'Fecha'),
            sortable: true,
            customRender: (parameterValue) => <CustomRenderDate value={parameterValue} />
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            searchKey: 'PayMCopyDate.status',
            sortable: true,
            customRender: (parameterValue) => <DisplayStatus text={collectionAndPaymentStatus[parseInt(parameterValue) as PayMCopyDateStatusKeys]?.name} colorText={collectionAndPaymentStatus[parseInt(parameterValue) as PayMCopyDateStatusKeys]?.colorText} icon={collectionAndPaymentStatus[parseInt(parameterValue) as PayMCopyDateStatusKeys]?.icon} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: collectionAndPaymentStatus, label: t('status', 'Estado') }
            },
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('totalAmount', 'Importe total'),
            customRender: (parameterValue, allRowData) => <CustomRenderDisplayPriceWOParams value={parameterValue} CurrencyId={allRowData?.CurrencyId} />
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'edit', allRowData })} />,
            havePermission: permissionCheck(userPermissions, 'update')
        },
    ]
    return (
        <TwinTable name='collectionAndPayment' columns={columns} getDataFrom='/api/app/accounting/collectionAndPayments/getAllCollectionAndPayments' setTableInstance={setTableInstance} onRowClick={(_idk, allRowData) => setOpened({ type: 'edit', allRowData })} extraWhereSubstituibleParams={{ 'PayMCopyDate.status': { '$notIn': [payMCopyDateStatusInversed['creating']] } }} defaultOrder={{ order: 'asc', defaultOrderField: 'date' }} />
    )
}

export default CollectionAndPayment